import {
  AnyAction,
  ActionReducerMapBuilder,
  AsyncThunk,
  createAsyncThunk,
  PayloadAction,
} from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { CreateAsyncActions } from '@modules/helpers';

import { loginActions } from './login';

type GenericAsyncThunk = AsyncThunk<unknown, unknown, any>;

type PendingAction = ReturnType<GenericAsyncThunk['pending']>;
type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;
type FulfilledAction = ReturnType<GenericAsyncThunk['fulfilled']>;

const isPendingAction = (action: AnyAction): action is PendingAction =>
  action.type.endsWith('/pending');
const isFulfilledAction = (action: AnyAction): action is FulfilledAction =>
  action.type.endsWith('/fulfilled');
const isRejectedAction = (action: AnyAction): action is RejectedAction =>
  action.type.endsWith('/rejected');

const filter =
  (...args) =>
  action =>
    args.every(f => f(action));

// error

type TApiError = {
  name: string;
  message: string;
  payload: ApiErrorPayloadAction;
};

type ErrorBuilderType = (
  name: string,
  builder: ActionReducerMapBuilder<{ errors: Record<string, unknown> }>,
  ignoreList?: AsyncThunk<any, any, Parameters<Parameters<typeof createAsyncThunk>[1]>[1]>[]
) => void;

type ApiErrorPayloadAction = {
  status: number;
  name: string;
  message: string;
  code: number | string;
  response: AxiosError<ErrorBuilderType>;
  data: Record<string, unknown> | string[];
};

export const errorBuilder: ErrorBuilderType = (name, builder, ignoreList) => {
  const checkName = (action: PayloadAction): boolean =>
    (action.type.startsWith(`${name}/`) || name === '*') && action.type.endsWith('/rejected');

  const isNotIgnored = (action: PayloadAction): boolean =>
    !ignoreList?.map(act => act.rejected.type)?.includes(action.type);

  builder.addMatcher(
    filter(checkName, isNotIgnored),
    (state, action: PayloadAction<ApiErrorPayloadAction, string, any, TApiError>) => {
      let errors: {
        message?: string;
        limits?: string;
        data?: string[];
      };
      const { payload } = action;
      if (!!payload && (payload.status === 404 || payload.code === CONST.errorCodes.ERR_NETWORK)) {
        return;
      }

      if (payload instanceof Error && !['ApiError', 'RequestError'].includes(payload.name)) {
        errors = { message: `${payload.name}: ${payload.message}.` };
      } else {
        errors = payload?.response || payload || {};
      }
      state.errors = { ...state.errors, ...errors };
    }
  );
};

// loadersBuilder

export type LoadersType<T extends { [key in string] }> = { [key in keyof T]?: boolean };

export const loadersBuilder = (
  name: string,
  builder: ActionReducerMapBuilder<{
    loaders: { [key in keyof LoadersType<ReturnType<CreateAsyncActions>>]?: boolean };
  }>
): void => {
  const getActionName = (action: PayloadAction): keyof ReturnType<CreateAsyncActions> =>
    action.type.split('/')[1];

  const checkName = (action: PayloadAction) => action.type.startsWith(`${name}/`);

  builder.addMatcher(filter(checkName, isPendingAction), (state, action: PayloadAction) => {
    const actionName = getActionName(action);
    state.loaders = { ...state.loaders, [actionName]: true };
  });

  builder.addMatcher(filter(checkName, isFulfilledAction), (state, action: PayloadAction) => {
    const actionName = getActionName(action);
    state.loaders = { ...state.loaders, [actionName]: false };
  });

  builder.addMatcher(filter(checkName, isRejectedAction), (state, action: PayloadAction) => {
    const actionName = getActionName(action);
    state.loaders = { ...state.loaders, [actionName]: false };
  });
};

export const logoutBuilder = <T extends { [key in string] }>(
  initialState: T,
  builder: ActionReducerMapBuilder<T>,
  ignoreList?: Array<keyof T>
): void => {
  const checkName = (action: PayloadAction) => action.type === loginActions.logout.fulfilled.type;

  builder.addMatcher(filter(checkName, isFulfilledAction), state => {
    const newObj: T = ignoreList?.reduce<any>((acc: T, el) => {
      const newState = state as T;
      if (newState[el]) acc[el] = newState[el];
      return acc;
    }, {});

    return { ...initialState, ...newObj };
  });
};
